<template>
    <div class="multiselect-wrapper">
        <div class="multiselect" @click="toggleDropdown" :class="{ 'multiselect--active': isOpen }">
            <div class="multiselect__tags">
                <div class="multiselect__tags-wrap">
                    <span v-if="selectedItems.length === 0" class="multiselect__placeholder">
                        {{ placeholder }}
                    </span>
                    <span v-else class="multiselect__tag" v-for="(item, index) in selectedItems" :key="index">
                        {{ getLabel(item) }}
                        <i class="bi bi-x" @click.stop="removeItem(item)"></i>
                    </span>
                </div>
                <i :class="isOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="multiselect__caret"></i>
            </div>
        </div>
        <div v-if="isOpen" class="multiselect__content">
            <div class="multiselect__input-wrapper" v-if="searchable">
                <input
                    v-model="searchQuery"
                    type="text"
                    class="multiselect__input"
                    :placeholder="`Buscar ${placeholder.toLowerCase()}...`"
                    @click.stop
                />
            </div>
            <ul class="multiselect__options">
                <li
                    v-for="option in filteredOptions"
                    :key="getValue(option)"
                    class="multiselect__option"
                    :class="{ 'multiselect__option--selected': isSelected(option) }"
                    @click.stop="toggleOption(option)"
                >
                    <span>{{ getLabel(option) }}</span>
                    <i v-if="isSelected(option)" class="bi bi-check"></i>
                </li>
                <li v-if="filteredOptions.length === 0" class="multiselect__option multiselect__option--disabled">
                    Nenhuma opção encontrada
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MultiselectComponent',
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        options: {
            type: Array,
            required: true
        },
        optionValue: {
            type: String,
            default: 'id'
        },
        optionLabel: {
            type: String,
            default: 'nome'
        },
        placeholder: {
            type: String,
            default: 'Selecione...'
        },
        searchable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpen: false,
            searchQuery: ''
        };
    },
    computed: {
        selectedItems() {
            if (!Array.isArray(this.modelValue)) return [];
            return this.options.filter(opt => this.modelValue.includes(this.getValue(opt)));
        },
        filteredOptions() {
            if (!this.searchable || !this.searchQuery) return this.options;
            const query = this.searchQuery.toLowerCase();
            return this.options.filter(opt => {
                const label = this.getLabel(opt).toLowerCase();
                return label.includes(query);
            });
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen;
            if (!this.isOpen) {
                this.searchQuery = '';
            }
        },
        toggleOption(option) {
            const value = this.getValue(option);
            const currentValue = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
            
            if (currentValue.includes(value)) {
                const index = currentValue.indexOf(value);
                currentValue.splice(index, 1);
            } else {
                currentValue.push(value);
            }
            
            this.$emit('update:modelValue', currentValue);
        },
        removeItem(item) {
            const value = this.getValue(item);
            const currentValue = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
            const index = currentValue.indexOf(value);
            
            if (index > -1) {
                currentValue.splice(index, 1);
                this.$emit('update:modelValue', currentValue);
            }
        },
        isSelected(option) {
            const value = this.getValue(option);
            return Array.isArray(this.modelValue) && this.modelValue.includes(value);
        },
        getValue(option) {
            if (typeof option === 'object' && option !== null) {
                return option[this.optionValue] ?? option.id ?? option;
            }
            return option;
        },
        getLabel(option) {
            if (typeof option === 'object' && option !== null) {
                return option[this.optionLabel] ?? option.nome ?? option.name ?? String(option);
            }
            return String(option);
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.isOpen = false;
                this.searchQuery = '';
            }
        }
    }
};
</script>

<style scoped>
.multiselect-wrapper {
    position: relative;
    width: 100%;
}

.multiselect {
    min-height: 38px;
    border: 1px solid var(--cor-borda-input, #ccc);
    border-radius: 6px;
    background-color: var(--cor-bg-input, #fff);
    cursor: pointer;
    transition: border-color 0.2s ease;
}

.multiselect:hover {
    border-color: var(--cor-primaria, #3697d3);
}

.multiselect--active {
    border-color: var(--cor-primaria, #3697d3);
    box-shadow: 0 0 0 2px rgba(54, 151, 211, 0.1);
}

.multiselect__tags {
    padding: 0.5rem 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 38px;
}

.multiselect__tags-wrap {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.multiselect__placeholder {
    color: var(--cor-fonte-secundaria, #999);
    font-size: 0.9rem;
}

.multiselect__tag {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background-color: var(--cor-primaria, #3697d3);
    color: white;
    border-radius: 4px;
    font-size: 0.85rem;
}

.multiselect__tag i {
    cursor: pointer;
    font-size: 0.9rem;
}

.multiselect__tag i:hover {
    opacity: 0.8;
}

.multiselect__caret {
    color: var(--cor-fonte-secundaria, #666);
    font-size: 1rem;
    margin-left: 0.5rem;
}

.multiselect__content {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.25rem;
    background-color: white;
    border: 1px solid var(--cor-borda-input, #ccc);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    max-height: 300px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.multiselect__input-wrapper {
    padding: 0.5rem;
    border-bottom: 1px solid var(--cor-separador, #eee);
}

.multiselect__input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--cor-borda-input, #ccc);
    border-radius: 4px;
    font-size: 0.9rem;
}

.multiselect__options {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    max-height: 250px;
}

.multiselect__option {
    padding: 0.75rem 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: background-color 0.2s ease;
}

.multiselect__option:hover {
    background-color: var(--cor-bg-secundario, #f8f9fa);
}

.multiselect__option--selected {
    background-color: rgba(54, 151, 211, 0.1);
    color: var(--cor-primaria, #3697d3);
    font-weight: 500;
}

.multiselect__option--selected:hover {
    background-color: rgba(54, 151, 211, 0.2);
}

.multiselect__option--disabled {
    color: var(--cor-fonte-secundaria, #999);
    cursor: not-allowed;
}

.multiselect__option i {
    color: var(--cor-primaria, #3697d3);
}
</style>

